import { useT } from '../../i18n';

/**
 * The one thing nobody guesses: on a Tesla the LEFT scroll wheel changes gear once the box is in
 * manual. It is drawn here, not photographed — our own wheel, our own red on the button you press —
 * and it shows up the moment the driver puts the box in manual, then gets out of the way.
 */
const KEY = 'roardrive:wheelhint:v1';

/** false once the driver has said they never want to see it again */
export function wheelHintWanted(): boolean {
  try { return localStorage.getItem(KEY) !== 'off'; } catch { return true; }
}
export function wheelHintNever(): void {
  try { localStorage.setItem(KEY, 'off'); } catch { /* private mode */ }
}

export function WheelHint({ onClose }: { onClose: () => void }) {
  const t = useT();
  return (
    <div class="whint" onClick={onClose}>
      <div class="whint__card" onClick={(e) => e.stopPropagation()}>
        <svg class="whint__art" viewBox="0 0 320 200" role="img" aria-label="Steering wheel">
          <defs>
            <linearGradient id="wh-rim" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#42464e" />
              <stop offset="0.5" stop-color="#26292f" />
              <stop offset="1" stop-color="#14161a" />
            </linearGradient>
            <linearGradient id="wh-hub" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#3d414a" />
              <stop offset="1" stop-color="#1d2025" />
            </linearGradient>
            <linearGradient id="wh-btn" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color="#ff6a4d" />
              <stop offset="1" stop-color="#e0290d" />
            </linearGradient>
            <radialGradient id="wh-glow">
              <stop offset="0" stop-color="#ff3d1f" stop-opacity="0.5" />
              <stop offset="1" stop-color="#ff3d1f" stop-opacity="0" />
            </radialGradient>
          </defs>
          {/* the rim */}
          <circle cx="160" cy="100" r="72" fill="none" stroke="url(#wh-rim)" stroke-width="18" />
          <circle cx="160" cy="100" r="63" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="1.5" />
          {/* the three spokes, then the hub on top of them */}
          <rect x="92" y="92" width="46" height="16" rx="8" fill="url(#wh-hub)" />
          <rect x="182" y="92" width="46" height="16" rx="8" fill="url(#wh-hub)" />
          <rect x="152" y="116" width="16" height="50" rx="8" fill="url(#wh-hub)" />
          <rect x="122" y="78" width="76" height="44" rx="14" fill="url(#wh-hub)" stroke="rgba(255,255,255,0.07)" />
          {/* the right thumb wheel, quiet */}
          <rect x="196" y="88" width="26" height="24" rx="9" fill="#2b2f35" stroke="#454a53" stroke-width="1.5" />
          <line x1="202" y1="94" x2="216" y2="94" stroke="#4c515a" stroke-width="1.5" />
          <line x1="202" y1="100" x2="216" y2="100" stroke="#4c515a" stroke-width="1.5" />
          <line x1="202" y1="106" x2="216" y2="106" stroke="#4c515a" stroke-width="1.5" />
          {/* THE LEFT ONE: this is the gear change */}
          <circle class="whint__halo" cx="111" cy="100" r="40" fill="url(#wh-glow)" />
          <circle class="whint__ring" cx="111" cy="100" r="22" fill="none" stroke="#ff3d1f" stroke-width="2" />
          <circle class="whint__ring whint__ring--2" cx="111" cy="100" r="22" fill="none" stroke="#ff3d1f" stroke-width="2" />
          <rect x="98" y="88" width="26" height="24" rx="9" fill="url(#wh-btn)" stroke="#ff8f79" stroke-width="1.5" />
          <circle class="whint__press" cx="111" cy="100" r="5" fill="#fff" />
        </svg>
        <div class="whint__title">{t('wheelHintTitle')}</div>
        <p class="whint__body">{t('wheelHintBody')}</p>
        {/* the rule itself: the press has no direction of its own, the car decides */}
        <div class="whint__rules">
          <div class="whint__rule"><span class="whint__arrow whint__arrow--up">▲</span>{t('wheelHintUp')}</div>
          <div class="whint__rule"><span class="whint__arrow whint__arrow--down">▼</span>{t('wheelHintDown')}</div>
        </div>
        <p class="whint__foot">{t('wheelHintPaddles')}</p>
        <button class="whint__ok" onClick={onClose}>{t('wheelHintOk')}</button>
        {/* and the way to never be asked again, quiet, where it belongs */}
        <button class="whint__never" onClick={() => { wheelHintNever(); onClose(); }}>{t('wheelHintNever')}</button>
      </div>
    </div>
  );
}
